<template>
  <div class="tag-details">
    <div class="details-eyebrow">Tag</div>
    <div class="details-title-row">
      <TagChip :name="editable ? editName : tag.name" :color="previewColor"
        :icon="editable ? editIcon : tag.icon" placeholder="Tag" />
      <span class="status-badge" :class="tag.isActive ? 'status-active' : 'status-inactive'">
        {{ tag.isActive ? 'Active' : 'Disabled' }}
      </span>
    </div>

    <div class="details-meta">
      <span class="meta-label">Group</span>
      <span class="meta-value">{{ tag.group || '—' }}</span>
    </div>
    <div v-if="tag.description && !editable" class="details-meta details-meta-block">
      <span class="meta-label">Description</span>
      <span class="meta-value">{{ tag.description }}</span>
    </div>
    <div v-if="tag.createdBy" class="details-meta">
      <span class="meta-label">Created by</span>
      <span class="meta-value">{{ tag.createdBy }}</span>
    </div>

    <template v-if="editable">
      <div class="edit-section">
        <v-text-field v-model="editName" label="Name" variant="outlined" density="comfortable" hide-details="auto"
          class="input-field" />
        <v-select v-model="editGroup" :items="groups" label="Group (optional)" variant="outlined"
          density="comfortable" hide-details="auto" class="input-field" clearable />
        <v-textarea v-model="editDescription" label="Description (optional)" variant="outlined" density="comfortable"
          hide-details="auto" class="input-field" rows="2" auto-grow />

        <div class="color-field">
          <span class="color-field-label">Colour</span>
          <div class="swatches">
            <button v-for="c in colorSwatches" :key="c" type="button" class="swatch"
              :class="{ 'is-selected': sameColor(c, editColor) }" :style="{ background: c }" :title="c" @click="editColor = c" />
            <label class="swatch custom-swatch" :style="{ background: editColor }" title="Custom colour">
              <v-icon size="14" color="#fff">mdi-plus</v-icon>
              <input type="color" v-model="editColor" class="color-input" />
            </label>
          </div>
        </div>

        <TagGlyphPicker v-model="editIcon" :color="editColor" />
      </div>

      <div class="details-actions">
        <button class="btn-primary" :disabled="saving || !editName.trim()" @click="save">
          {{ saving ? 'Working…' : 'Save' }}
        </button>
        <button v-if="tag.isActive" class="btn-danger" :disabled="saving" @click="$emit('toggle-active')">Disable</button>
        <button v-else class="btn-ghost" :disabled="saving" @click="$emit('toggle-active')">Enable</button>
      </div>
      <p class="hint-note">Disabling keeps the tag on existing items but hides it from the assign pickers.</p>
    </template>

    <div v-else class="readonly-note">
      <v-icon size="16" color="#8aa0aa">mdi-lock-outline</v-icon>
      <span>Only organization admins can edit or disable tags.</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { MARKER_COLOR_SWATCHES, DEFAULT_MARKER_COLOR } from '@/lib/markerShapeFactory';
import { TAG_GROUPS } from '@/constants/tagOptions';
import TagChip from '@/components/tags/TagChip.vue';
import TagGlyphPicker from '@/components/tags/TagGlyphPicker.vue';

const props = defineProps({
  tag: Object,
  editable: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['save', 'toggle-active']);

const groups = TAG_GROUPS;
const colorSwatches = MARKER_COLOR_SWATCHES;
const sameColor = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

const editName = ref(props.tag.name || '');
const editColor = ref(props.tag.color || DEFAULT_MARKER_COLOR);
const editGroup = ref(props.tag.group || null);
const editDescription = ref(props.tag.description || '');
const editIcon = ref(props.tag.icon || null);

const previewColor = computed(() => editColor.value || DEFAULT_MARKER_COLOR);

function save() {
  emit('save', {
    name: editName.value.trim(),
    color: editColor.value,
    group: editGroup.value || null,
    description: editDescription.value.trim() || null,
    icon: editIcon.value || null,
  });
}
</script>

<style scoped>
.tag-details { padding: 8px; color: #fff; display: flex; flex-direction: column; gap: 10px; min-width: min(380px, 80vw); }
.details-eyebrow { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8aa0aa; }
.details-title-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.details-meta { display: flex; align-items: center; gap: 10px; }
.details-meta-block { align-items: flex-start; flex-direction: column; gap: 2px; }
.meta-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.08em; color: #8aa0aa; font-weight: 700; }
.meta-value { font-size: 0.9rem; color: #cfd8dc; line-height: 1.4; }

.status-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; font-size: 0.7rem; font-weight: 700; }
.status-active { background: rgba(74, 222, 128, 0.12); border: 1px solid rgba(74, 222, 128, 0.3); color: #4ade80; }
.status-inactive { background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.3); color: #f87171; }

.edit-section { display: flex; flex-direction: column; gap: 12px; margin-top: 6px; }
.input-field :deep(.v-field) { border-radius: 10px; background: rgba(255, 255, 255, 0.03); }

.color-field { display: flex; flex-direction: column; gap: 8px; }
.color-field-label { font-size: 0.78rem; color: #b0bec5; font-weight: 600; }
.swatches { display: flex; flex-wrap: wrap; gap: 8px; }
.swatch {
  width: 30px; height: 30px; border-radius: 8px; border: 2px solid rgba(255, 255, 255, 0.12);
  cursor: pointer; padding: 0; display: inline-flex; align-items: center; justify-content: center;
  transition: transform 0.1s ease, border-color 0.1s ease;
}
.swatch:hover { transform: scale(1.08); }
.swatch.is-selected { border-color: #fff; box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.5); }
.custom-swatch { position: relative; overflow: hidden; }
.color-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }

.details-actions { display: flex; gap: 10px; margin-top: 12px; }
.hint-note { font-size: 0.78rem; color: #8aa0aa; margin: 4px 0 0; line-height: 1.4; }
.readonly-note { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; color: #8aa0aa; margin-top: 4px; }

.btn-primary {
  padding: 10px 18px; border-radius: 10px; background: var(--duallo-accent-gradient); color: #071514;
  font-weight: 700; border: none; cursor: pointer; font: inherit;
}
.btn-danger {
  padding: 10px 18px; border-radius: 10px; background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171; font-weight: 700; cursor: pointer; font: inherit;
}
.btn-ghost {
  padding: 10px 18px; border-radius: 10px; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.12);
  color: #cfd8dc; font-weight: 700; cursor: pointer; font: inherit;
}
.btn-primary:disabled, .btn-danger:disabled, .btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
